import { FetchMoreOptions, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";

import NotFoundPage from "components/error/NotFoundPage";
import FiltersSortHeaderStatic from "components/Filters/SortHeader/Static";
import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLoading from "components/loading/PageLoading";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { NamedWebhooksIntegration, WebhookDelivery } from "types/generated";

import { COLUMNS_SIZE } from "./constants";
import { WebhookContext } from "./Context";
import { GET_WEBHOOK } from "./gql";
import WebhookDeliveryVirtualizedListItem from "./ListItem/Virtualized";
import styles from "./styles.module.css";
import useIntegrationsLayout from "./useIntegrationsLayout";

const WebhookDeliveries = () => {
  const cachedWebhookDeliveriesNodes = useRef<WebhookDelivery[]>([]);
  const { integrationId: webhookId } = useParams<{ integrationId: string }>();
  const { onError } = useTypedContext(FlashContext);
  const [token, setToken] = useState<string | null | undefined>(null);

  const onCompleted = useCallback(
    (data: { namedWebhooksIntegration: NamedWebhooksIntegration }) => {
      const { namedWebhooksIntegration } = data;

      if (namedWebhooksIntegration?.deliveries?.nextToken !== token) {
        setToken(namedWebhooksIntegration?.deliveries?.nextToken);
      } else {
        setToken(null);
      }
    },
    [token]
  );

  const {
    error,
    fetchMore: fetchMoreWebhookDeliveries,
    loading,
    data,
  } = useQuery<{
    namedWebhooksIntegration: NamedWebhooksIntegration;
  }>(GET_WEBHOOK, {
    variables: {
      id: webhookId,
      nextToken: null,
    },
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  useEffect(() => {
    if (data) {
      onCompleted(data);
    }
  }, [data, onCompleted]);

  useTitle(`Webhook · ${data?.namedWebhooksIntegration?.name ?? ""}`);

  const [memoizedWebhookDelivery] = useMemo(() => {
    const sourceEdges = data?.namedWebhooksIntegration?.deliveries?.nodes || [];

    const edges =
      loading && !sourceEdges.length ? cachedWebhookDeliveriesNodes.current : sourceEdges;

    if (!loading) {
      cachedWebhookDeliveriesNodes.current = sourceEdges;
    }

    return [edges];
  }, [data?.namedWebhooksIntegration?.deliveries?.nodes, loading]);

  const updateQuery: FetchMoreOptions<{
    namedWebhooksIntegration: NamedWebhooksIntegration;
  }>["updateQuery"] = (prev, { fetchMoreResult }) => {
    if (!fetchMoreResult) return prev;

    if (
      fetchMoreResult &&
      prev?.namedWebhooksIntegration.deliveries?.nodes &&
      fetchMoreResult?.namedWebhooksIntegration.deliveries?.nodes
    ) {
      if (fetchMoreResult?.namedWebhooksIntegration.deliveries?.nextToken) {
        setToken(fetchMoreResult?.namedWebhooksIntegration.deliveries?.nextToken);
      }
      return {
        namedWebhooksIntegration: {
          ...fetchMoreResult.namedWebhooksIntegration,

          deliveries: {
            ...fetchMoreResult.namedWebhooksIntegration.deliveries,
            nextToken: fetchMoreResult.namedWebhooksIntegration.deliveries?.nextToken,
            nodes: [
              ...(prev?.namedWebhooksIntegration.deliveries?.nodes || []),
              ...(fetchMoreResult?.namedWebhooksIntegration.deliveries?.nodes || []),
            ],
          },
        },
      };
    }

    return { namedWebhooksIntegration: { ...prev.namedWebhooksIntegration } };
  };

  const loadMoreItems = async () => {
    try {
      if (data?.namedWebhooksIntegration && data?.namedWebhooksIntegration?.deliveries?.nextToken) {
        await fetchMoreWebhookDeliveries({
          updateQuery,
          variables: {
            id: webhookId,
            nextToken: token,
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  };

  useIntegrationsLayout(data?.namedWebhooksIntegration);

  const isItemLoaded = (value: number) => value < memoizedWebhookDelivery.length;

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.namedWebhooksIntegration) {
    return <PageLoading />;
  }

  if (!data?.namedWebhooksIntegration) {
    return <NotFoundPage />;
  }

  return (
    <WebhookContext.Provider
      value={{
        webhook: data.namedWebhooksIntegration,
      }}
    >
      <WebhooksTierInfo />
      <Box direction="column" grow="1" fullWidth relative>
        <PageInfoHeader title="Recent deliveries" className={styles.pageInfo} />
        <FiltersSortHeaderStatic
          sortOptions={["Delivery ID", "Status code", "Delivered at"]}
          columnOrder={COLUMNS_SIZE}
        />
        {data && memoizedWebhookDelivery.length > 0 && (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={memoizedWebhookDelivery.length + 100}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered }) => (
              <ListEntitiesNew
                itemCount={memoizedWebhookDelivery.length}
                itemProps={{
                  items: memoizedWebhookDelivery,
                  selectedSet: new Set(""),
                }}
                virtualizedItem={WebhookDeliveryVirtualizedListItem}
                itemKey={(index) => memoizedWebhookDelivery[index].id}
                onItemsRendered={onItemsRendered}
              />
            )}
          </InfiniteLoader>
        )}
      </Box>
    </WebhookContext.Provider>
  );
};

export default WebhookDeliveries;
